/* eslint-disable camelcase */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { Alert, Box, Button, CircularProgress, Dialog, Fade, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, Close } from '@mui/icons-material';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { getAllSendingOrderProduct, saveSendingOrder } from 'src/shared/services/sendingService';
import { SendingOrderProduct } from 'src/modules/sending/models/sendingOrderProduct';
import { getAllOrganizations } from 'src/shared/services/organizationService';
import { SendingOrderCreate, SendingOrderCreateSchema } from 'src/modules/sending/models/sendingOrderCreate';
import { SendingOrderStatusEnum } from 'src/modules/sending/models/sendingOrderStatus';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import { FormSelectableStateProvider } from 'src/shared/components/form/formSelectableStateContext';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { SendingWizardIntro } from 'src/modules/sending/components/sendingWizardIntro';
import { SendingWizardStep1Details } from 'src/modules/sending/components/sendingWizardStep1Details';
import { SendingDatePicker } from 'src/modules/sending/components/sendingDatePicker';
import { SendingFriPeriodTimeline } from 'src/modules/sending/components/sendingFriPeriodTimeline';
import { deriveMembershipYear } from 'src/modules/sending/functions/membershipYear';

const isEmpty = (v: unknown) => v === '' || v === null || v === undefined;
const STEP_COUNT = 3;

// Fas A: en 3-stegs-modal som samlar beställare + utskicksdatum + förfallodatum. "Spara och gå vidare" sparar
// beställningen som Pågående och navigerar till redigeringen (fas B). Stegen tonas in mjukt vid bläddring.
const SendingOrderCreatePageInner = () => {
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const [saving, setSaving] = useState(false);
  // Intro-vy först (välkomst), sedan de tre stegen.
  const [intro, setIntro] = useState(true);
  const [step, setStep] = useState(0);
  // Visar en spinner mellan stegen tills nästa steg fadar in (mjukare än direkt-fade).
  const [transitioning, setTransitioning] = useState(false);
  const [sendDate, setSendDate] = useState<Dayjs | null>(null);

  // Förbundets förfallodatum (MMdd) + friperiod (dagar) – styr härlett medlemsår och friperiod-tidslinjen i steg 3.
  const [membershipMMdd, setMembershipMMdd] = useState<string | null>(null);
  const [gracePeriodDays, setGracePeriodDays] = useState(0);

  const { handleSubmit, watch, setValue, trigger } = useFormContext<SendingOrderCreate>();

  useEffect(() => {
    const fetchOrg = async () => {
      try {
        const res = await getAllOrganizations();
        const org = res.data.results?.[0];
        if (isMounted() && org) {
          const raw = (org.expireDate ?? '').trim();
          setMembershipMMdd(/^\d{4}$/.test(raw) ? raw : null);
          setGracePeriodDays(Number(org.gracePeriod) || 0);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('error getAllOrganizations', e);
      }
    };
    fetchOrg();
  }, [isMounted]);

  const payDateStr = watch('payDate') as string | undefined;
  const payDate = payDateStr ? dayjs(payDateStr) : null;
  const dateId = watch('date_id') as number | string | undefined;

  const result = useMemo(
    () => deriveMembershipYear({ sendDate, payDate, expireMMdd: membershipMMdd, graceDays: gracePeriodDays }),
    [sendDate, payDate, membershipMMdd, gracePeriodDays],
  );
  const payBeforeSend = !!(sendDate && payDate && payDate.isBefore(sendDate, 'day'));
  const straddles = !!result?.straddles;
  const fmt = (d: Dayjs) => d.format('YYYY-MM-DD');

  // Utskickstyperna. Påminnelser är led på en beställning, inte egna typer, så de räknas inte med här.
  // Finns bara en typ sätts den direkt på beställningen – annars skulle ordern sakna typ tills guiden öppnas,
  // och supportuppdraget bokas som "Övrigt".
  const [products, setProducts] = useState<SendingOrderProduct[]>([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productResult = await getAllSendingOrderProduct();
        const selectable = (productResult.data ?? []).filter((product) => !(product.name ?? '').toLowerCase().includes('påminnel'));
        if (isMounted()) setProducts(selectable);
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('error getAllSendingOrderProduct', e);
      }
    };
    fetchProducts();
  }, [isMounted]);

  // Utskickstyp för den nya beställningen: kundens val om det finns, annars den enda typen (om det bara finns en).
  const resolveProductId = useCallback(
    (chosen: number | undefined): number | undefined => {
      if (chosen) return Number(chosen);
      if (products.length === 1) return products[0].id;
      return undefined;
    },
    [products],
  );

  const handleClose = useCallback(() => navigate(RoutesEnum.Sending), [navigate]);

  const onSubmit = useCallback(
    async (data: SendingOrderCreate) => {
      const dataToPost: SendingOrderCreate = {
        ...data,
        date_id: data.date_id ? Number(data.date_id) : undefined,
        owner_id: data.owner_id ? Number(data.owner_id) : undefined,
        payDate: data.payDate ? dayjs(data.payDate).format('YYYY-MM-DD') : undefined,
        color: data.color ?? true,
        status: SendingOrderStatusEnum.Pagaende,
        // Enda utskickstypen sätts direkt; finns flera väljer kunden i guidens första steg.
        product_id: resolveProductId(data.product_id),
      };

      try {
        setSaving(true);
        const res = await saveSendingOrder({ ...dataToPost, created: new Date() } as SendingOrderCreate);
        const newId = Number((res.data as unknown) ?? 0);
        if (isMounted()) {
          toast.success('Beställningen har sparats – fortsätt fylla i den här.');
          navigate(newId > 0 ? `${RoutesEnum.EditSendingOrder}/${newId}` : RoutesEnum.Sending);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('error saveSendingOrder', e);
      } finally {
        if (isMounted()) setSaving(false);
      }
    },
    [isMounted, navigate, resolveProductId],
  );

  // Får man gå vidare från aktuellt steg?
  const canProceed = (() => {
    if (step === 0) return !isEmpty(watch('owner_id')) && !isEmpty(watch('createdByName')) && !isEmpty(watch('createdByEmail'));
    if (step === 1) return !isEmpty(dateId);
    return !isEmpty(payDateStr) && !payBeforeSend && !straddles;
  })();

  // Byt steg: visa spinner en kort stund, byt sedan steg och låt det fada in.
  const goToStep = useCallback(
    (next: number) => {
      setTransitioning(true);
      window.setTimeout(() => {
        if (!isMounted()) return;
        setStep(next);
        setTransitioning(false);
      }, 300);
    },
    [isMounted],
  );

  const handleNext = useCallback(async () => {
    if (step === 0) {
      const ok = await trigger(['createdByName', 'createdByEmail']);
      if (!ok) return;
    }
    goToStep(Math.min(STEP_COUNT - 1, step + 1));
  }, [step, trigger, goToStep]);

  // Starta guiden från intron: spinner en kort stund, sedan fadar steg 1 in.
  const handleStart = useCallback(() => {
    setTransitioning(true);
    window.setTimeout(() => {
      if (!isMounted()) return;
      setIntro(false);
      setTransitioning(false);
    }, 300);
  }, [isMounted]);

  const stepTitles = ['Vem beställer utskicket?', 'När ska fakturorna skickas ut?', 'När ska medlemmarna senast betala?'];

  // Innehållet för aktuellt steg (byts med mjuk in-toning via Fade + key).
  const renderStep = () => {
    if (step === 0) return <SendingWizardStep1Details />;
    if (step === 1) {
      return (
        <Box>
          <Typography variant='h5' sx={{ fontWeight: 700, mb: 3 }}>
            När ska fakturorna skickas ut?
          </Typography>
          <SendingDatePicker
            value={dateId}
            onChange={(id, date) => {
              setSendDate(date);
              setValue('date_id', id, { shouldValidate: true });
              // Föreslå förfallodatum = utskicksdatum + 35 dagar (får aldrig ligga före utskicket).
              setValue('payDate', date.add(35, 'day').format('YYYY-MM-DD'), { shouldValidate: true });
            }}
          />
        </Box>
      );
    }
    return (
      <Box>
        <Typography variant='h5' sx={{ fontWeight: 700, mb: 1 }}>
          När ska medlemmarna senast betala?
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Ange vilket förfallodatum som ska framgå på medlemmarnas fakturor.
        </Typography>
        <Box sx={{ maxWidth: { xs: '100%', sm: 280 } }}>
          <ControlledDatePicker name='payDate' label='Förfallodatum' minDate={sendDate ?? undefined} />
        </Box>

        {sendDate && result && (
          <>
            <Box sx={{ mt: 3, maxWidth: 640 }}>
              <SendingFriPeriodTimeline sendDate={sendDate} payDate={payDate} result={result} />
            </Box>

            {payBeforeSend && (
              <Alert severity='error' sx={{ mt: 2 }}>
                Sista betaldatum får inte ligga före utskicksdatumet.
              </Alert>
            )}

            {straddles && !payBeforeSend && (
              <Alert severity='error' sx={{ mt: 2 }}>
                Friperioden börjar {fmt(result.friStart)} och hamnar då inom betalningsfönstret. Då skulle medlemmar som betalar tidigt få medlemsår{' '}
                {result.expiryYear - 1} och de som betalar under friperioden få {result.expiryYear} – från samma avisering. Välj ett sista betaldatum
                senast {fmt(result.latestSafePayDate)} (eller flytta hela utskicket in i friperioden).
              </Alert>
            )}

            {!straddles && !payBeforeSend && (
              <Alert severity='info' sx={{ mt: 2 }}>
                Aviseringen gäller <b>medlemsår {result.expiryYear}</b> (förfallodatum {fmt(result.expiryDate)}).
                {result.hasFriPeriod ? ` Friperioden börjar ${fmt(result.friStart)}.` : ' Förbundet har ingen friperiod.'}
              </Alert>
            )}

            {!straddles && !payBeforeSend && result.latePaymentSpill && (
              <Alert severity='warning' sx={{ mt: 2 }}>
                Obs: sena betalningar som kommer in under friperioden (fr.o.m. {fmt(result.friStart)}) ger nästa års medlemskap (
                {result.expiryYear + 1}
                ). Vill du undvika det kan du välja ett sista betaldatum med god marginal före friperioden.
              </Alert>
            )}
          </>
        )}
      </Box>
    );
  };

  // Kroppens innehåll: spinner under byte, annars intro eller aktuellt steg – båda fadar in.
  const renderBody = () => {
    if (transitioning) return <CircularProgress />;
    if (intro) {
      return (
        <Fade in key='intro' timeout={400}>
          <Box sx={{ width: '100%' }}>
            <SendingWizardIntro onStart={handleStart} />
          </Box>
        </Fade>
      );
    }
    return (
      <Fade in key={step} timeout={400}>
        <Box sx={{ width: '100%', maxWidth: 760 }}>{renderStep()}</Box>
      </Fade>
    );
  };

  const isLastStep = step === STEP_COUNT - 1;

  // Fullskärm på laptops/mindre skärmar (< xl) så hela höjden kan användas; på stora skärmar en stor centrerad modal.
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xl'));

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth='lg'
      fullWidth
      fullScreen={fullScreen}
      sx={{
        '& .MuiDialog-paper': {
          display: 'flex',
          flexDirection: 'column',
          height: fullScreen ? '100%' : '80vh',
          bgcolor: 'background.paper',
        },
      }}
    >
      {/* Topbar: titel + stegindikator + stäng. */}
      <Box sx={{ flexShrink: 0, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, sm: 3 }, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant='subtitle1' sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Beställ fakturautskick
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {intro ? 'Så här fungerar det' : `Steg ${step + 1} av ${STEP_COUNT} · ${stepTitles[step]}`}
            </Typography>
          </Box>
          <IconButton aria-label='Stäng' onClick={handleClose} sx={{ mt: -0.5, mr: -1 }}>
            <Close />
          </IconButton>
        </Box>
        {/* Segmenterad progress-rad (dold på intron). */}
        {!intro && (
          <Box sx={{ display: 'flex', gap: 0.75, mt: 1.25 }}>
            {Array.from({ length: STEP_COUNT }).map((_v, i) => (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: i <= step ? 'primary.main' : 'grey.300', transition: 'background-color .2s' }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Innehåll – intro / spinner under bytet / aktuellt steg som fadar in. Vit bakgrund, centrerat. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', bgcolor: 'background.paper', px: { xs: 2, sm: 3 }, py: 3 }}>
        <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>{renderBody()}</Box>
      </Box>

      {/* Footer: Tillbaka + Nästa / Spara och gå vidare. Dold på intron (som har egen startknapp). */}
      {!intro && (
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            px: { xs: 2, sm: 3 },
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.08)',
          }}
        >
          {step > 0 ? (
            <Button type='button' variant='outlined' size='large' onClick={() => goToStep(step - 1)} startIcon={<ArrowBackIos fontSize='small' />}>
              Tillbaka
            </Button>
          ) : (
            <Box />
          )}
          {!isLastStep ? (
            <Button
              type='button'
              variant='contained'
              size='large'
              disableElevation
              disabled={!canProceed}
              onClick={handleNext}
              endIcon={<ArrowForwardIos fontSize='small' />}
            >
              Nästa
            </Button>
          ) : (
            <Button
              type='button'
              variant='contained'
              size='large'
              disableElevation
              disabled={saving || !canProceed}
              onClick={handleSubmit(onSubmit)}
              endIcon={<ArrowForwardIos fontSize='small' />}
            >
              Spara och gå vidare
            </Button>
          )}
        </Box>
      )}
    </Dialog>
  );
};

const SendingOrderCreatePage = () => {
  const formMethods = useForm<SendingOrderCreate>({
    resolver: yupResolver(SendingOrderCreateSchema),
    mode: 'all',
    defaultValues: {
      color: true,
      status: SendingOrderStatusEnum.Pagaende,
      createdByName: '',
      createdByEmail: '',
      payDate: dayjs().add(30, 'day').format('YYYY-MM-DD'),
    },
  });

  return (
    <FormProvider {...formMethods}>
      <FormSelectableStateProvider>
        <SendingOrderCreatePageInner />
      </FormSelectableStateProvider>
    </FormProvider>
  );
};

export default SendingOrderCreatePage;
